/* eslint-disable unicorn/consistent-destructuring */
import angular from 'angular';
import _ from 'lodash-es';

/**
 * @param {any} $scope
 * @param {import('angular').IInterpolateService}$interpolate
 * @param {import('angular').IQService} $q
 * @param {import('angular').ITimeoutService} $timeout
 */
export default /* @ngInject */ (
	$scope,
	$interpolate,
	$q,
	$timeout,
) => {
	// FOR-71 - Skip functionality in the options.building view.
	if ($scope.options && $scope.options.building) {
		return;
	}
	const settings = $scope.component;
	const options = {};
	$scope.nowrap = true;
	$scope.hasNextPage = false;
	$scope.selectItems = [];

	if (settings.autofocus) {
		$timeout(() => {
			const inputs = angular.element(`#form-group-${settings.key}`)
				.find('input');
			inputs[settings.validate?.card?.isMultiple() ? 0 : 1].focus();
		});
	}

	const initialized = $q.defer();
	initialized.promise
		.then(() => $scope.$emit('selectLoaded', $scope.component))
		.catch(error => console.error(error));

	const { selectValues } = $scope.component;
	const { valueProperty } = $scope.component;
	$scope.getSelectItem = (/** @type {any} */ item) => {
		if (!item) {
			return '';
		}
		if (settings.dataSrc === 'nodes') {
			return item.kid;
		}
		if (settings.dataSrc === 'values') {
			return item.value;
		}

		// Get the item value.
		const itemValue = valueProperty ? _.get(item, valueProperty) : item;
		if (itemValue === undefined) {
			/* eslint-disable no-console */
			console.warn(`Cannot find value property within select: ${valueProperty}`);
			/* eslint-enable no-console */
		}
		return itemValue;
	};

	$scope.refreshItems = () => $q.resolve([]);
	$scope.$on('refreshList', (
		/** @type {Event} */ event,
		/** @type {string} */ url,
		/** @type {string} */ input,
	) => {
		$scope.refreshItems(input, url);
	});

	let refreshing = false;
	const refreshValue = () => {
		if (refreshing) {
			return;
		}
		refreshing = true;
		const temporaryData = $scope.data[settings.key];
		$scope.data[settings.key] = settings.validate?.card?.isMultiple() ? [] : '';
		if (settings.clearOnRefresh) {
			refreshing = false;
			$scope.$emit('selectLoaded', $scope.component);
			const index = $scope.selectItems.indexOf(temporaryData);
			if (index !== -1) {
				$scope.selectItems.splice(index, 1);
			}
		}
		else {
			$timeout(() => {
				$scope.data[settings.key] = temporaryData;
				refreshing = false;
				$scope.$emit('selectLoaded', $scope.component);
			});
		}
	};

	// Ensures that the value is within the select items.
	const ensureValue = (/** @type {any} */ value) => {
		// eslint-disable-next-line no-param-reassign
		value = _.isNil(value) ? $scope.data[settings.key] : value;
		if (_.isNil(value) || (Array.isArray(value) && value.length === 0)) {
			return;
		}
		if (Array.isArray(value)) {
			// eslint-disable-next-line unicorn/no-array-for-each,unicorn/no-array-callback-reference
			value.forEach(ensureValue);
			return;
		}
		// Iterate through the list of items and see if our value exists...
		let found = false;
		for (let index = 0; index < $scope.selectItems.length; index++) {
			const item = $scope.selectItems[index];
			const selectItem = $scope.getSelectItem(item);
			if (_.isEqual(selectItem, value)) {
				found = true;
				break;
			}
		}

		// If the item is not found in the select items array, then add it manually.
		if (!found) {
			let itemValue = value;
			if (valueProperty) {
				itemValue = {};
				_.set(itemValue, valueProperty, value);
			}
			$scope.selectItems.push(itemValue);
		}
	};

	// Refresh the items when ready.
	const refreshItemsWhenReady = () => {
		initialized.promise
			.then(() => {
				const refreshPromise = $scope.refreshItems(true);
				if (refreshPromise) {
					refreshPromise
						// eslint-disable-next-line promise/no-nesting
						.then(refreshValue)
						// eslint-disable-next-line promise/no-nesting
						.catch((/** @type {any} */ error) => console.error(error));
				}
				else {
					refreshValue();
				}
				return true;
			})
			.catch(error => console.error(error));
	};

	// Add a watch if they wish to refresh on selection of another field.
	if (settings.refreshOn) {
		if (settings.refreshOn === 'data') {
			$scope.$watch('data', refreshItemsWhenReady, true);
			return;
		}

		$scope.$watch(`data.${settings.refreshOn}`, refreshItemsWhenReady);
		$scope.$watch(`submission.data.${settings.refreshOn}`, refreshItemsWhenReady);
	}
	else {
		// Watch for the data to be set, and ensure the value is set properly.
		const dataWatch = $scope.$watch(`data.${settings.key}`, (/** @type {any} */ value) => {
			if (value) {
				initialized.promise
					.then(() => {
						dataWatch();
						return ensureValue();
					})
					.catch(error => console.error(error));
			}
		});
	}

	/** @type {any} */
	let lastInput;
	switch (settings.dataSrc) {
	case 'nodes':
	case 'values': {
		$scope.selectItems = settings.data?.values;
		initialized.resolve();
		break;
	}
	case 'json': {
		/** @type {any} */
		let items;

		// Set the new result.
		const setResult = (/** @type {any} */ data, /** @type {boolean} */ append) => {
			// coerce the data into an array.
			if (!Array.isArray(data)) {
				// eslint-disable-next-line no-param-reassign
				data = [data];
			}

			if (data.length < options.params.limit) {
				$scope.hasNextPage = false;
			}
			$scope.selectItems = append ? [
				...$scope.selectItems,
				...data,
			] : data;
		};

		try {
			if (typeof $scope.component.data.json === 'string') {
				items = angular.fromJson($scope.component.data.json);
			}
			else if (typeof $scope.component.data.json === 'object') {
				items = $scope.component.data.json;
			}
			else {
				items = [];
			}

			if (selectValues) {
				// Allow dot notation in the selectValue property.
				if (selectValues.includes('.')) {
					const parts = selectValues.split('.');
					let select = items;
					// eslint-disable-next-line guard-for-in
					for (const index in parts) {
						select = select[parts[index]];
					}
					items = select;
				}
				else {
					items = items[selectValues];
				}
			}
		}
		catch (error) {
			/* eslint-disable no-console */
			console.warn(`Error parsing JSON in ${$scope.component.key}`, error);
			/* eslint-enable no-console */
			items = [];
		}
		options.params = {
			limit: $scope.component.limit || 20,
			skip:  0,
		};

		$scope.refreshItems = (
			/** @type {string} */ input,
			/** @type {string} */ url,
			/** @type {boolean} */ append,
		) => {
			// If they typed in a search, reset skip.
			if (lastInput !== input) {
				lastInput = input;
				options.params.skip = 0;
			}
			let selectItems = items;
			if (input) {
				selectItems = selectItems.filter((/** @type {any} */ item) => {
					// Get the visible string from the interpolated item.
					const value = $interpolate($scope.component.template)({ item })
						.replaceAll(/<[\n.]*?>/gm, '');
					switch ($scope.component.filter) {
					case 'startsWith': {
						return value.toLowerCase()
							.lastIndexOf(input.toLowerCase(), 0) === 0;
					}
					case 'contains': {
						return value.toLowerCase()
							.includes(input.toLowerCase());
					}
					default: {
						return value.toLowerCase()
							.lastIndexOf(input.toLowerCase(), 0) === 0;
					}
					}
				});
			}
			// @ts-ignore
			options.params.skip = Number.parseInt(options.params.skip, 10);
			options.params.limit = Number.parseInt(options.params.limit, 10);
			selectItems = selectItems.slice(options.params.skip, options.params.skip + options.params.limit);
			setResult(selectItems, append);
			return initialized.resolve($scope.selectItems);
		};
		$scope.refreshItems();
		break;
	}
	case 'custom': {
		$scope.refreshItems = () => {
			try {
				/* eslint-disable no-unused-vars */
				const data = _.cloneDeep($scope.submission.data);
				const row = _.cloneDeep($scope.data);
				/* eslint-enable no-unused-vars */
				// eslint-disable-next-line no-eval
				$scope.selectItems = eval(`(function (data, row) {
							var values = [];
							${settings.data.custom.toString()};
							return values;
						})(data, row)`);
			}
			catch {
				$scope.selectItems = [];
			}
			return initialized.resolve($scope.selectItems);
		};
		$scope.refreshItems();
		break;
	}
	default: {
		$scope.selectItems = [];
		initialized.resolve($scope.selectItems);
	}
	}
};
